import { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Lock, Eye, EyeOff, CheckCircle2, AlertCircle, Loader2, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toUiLocale } from '@/i18n/config';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const errMsg = (e: any, fb: string) => {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((x: any) => x?.msg || '').filter(Boolean).join(' ') || fb;
  return fb;
};

export default function ResetPasswordPage() {
  const { t, i18n } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';
  // Iter 761: «Send innlogging»-lenken bærer kundens språk (?lang=en) så siden åpnes på riktig språk.
  const linkLang = toUiLocale(params.get('lang'));
  if (linkLang && i18n.language !== linkLang) i18n.changeLanguage(linkLang);

  const [pw, setPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (pw !== confirm) { toast.error(t('resetPassword.mismatch', 'Passordene stemmer ikke overens')); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: pw });
      setDone(true);
      toast.success(t('resetPassword.updated', 'Passordet er oppdatert'));
      setTimeout(() => navigate('/login'), 2500);
    } catch (e) {
      toast.error(errMsg(e, t('resetPassword.failed', 'Kunne ikke tilbakestille passordet')));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-8 shadow-xl" data-testid="reset-password-page">
        {!token ? (
          <div className="text-center space-y-3">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto" />
            <h1 className="text-xl font-bold">{t('resetPassword.invalidLink', 'Ugyldig lenke')}</h1>
            <p className="text-sm text-muted-foreground">{t('resetPassword.invalidLinkDesc', 'Lenken mangler en gyldig kode. Be om en ny tilbakestillingslenke.')}</p>
            <Button asChild className="w-full"><Link to="/forgot-password">{t('resetPassword.requestNew', 'Be om ny lenke')}</Link></Button>
          </div>
        ) : done ? (
          <div className="text-center space-y-3" data-testid="reset-success">
            <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto" />
            <h1 className="text-xl font-bold">{t('resetPassword.updated', 'Passordet er oppdatert')}</h1>
            <p className="text-sm text-muted-foreground">{t('resetPassword.redirecting', 'Du sendes nå til innloggingssiden…')}</p>
            <Button asChild className="w-full"><Link to="/login">{t('auth.toLogin', 'Til innlogging')}</Link></Button>
          </div>
        ) : (
          <>
            <Link to="/login" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6">
              <ArrowLeft className="w-4 h-4" /> {t('auth.backToLogin', 'Tilbake til innlogging')}
            </Link>
            <div className="mb-6">
              <h1 className="text-2xl font-bold">{t('resetPassword.title', 'Velg nytt passord')}</h1>
              <p className="text-sm text-muted-foreground mt-1">{t('resetPassword.requirements', 'Minst 8 tegn, én stor bokstav og ett tall.')}</p>
            </div>
            <form onSubmit={submit} className="space-y-4">
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  type={show ? 'text' : 'password'} required minLength={8} value={pw}
                  onChange={(e) => setPw(e.target.value)} placeholder={t('resetPassword.newPassword', 'Nytt passord')}
                  className="pl-10 pr-12 h-12" data-testid="reset-new-password-input"
                />
                <button type="button" onClick={() => setShow(!show)} aria-label={t('auth.togglePassword', 'Vis/skjul passord')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  {show ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  type={show ? 'text' : 'password'} required value={confirm}
                  onChange={(e) => setConfirm(e.target.value)} placeholder={t('resetPassword.confirmNewPassword', 'Bekreft nytt passord')}
                  className="pl-10 h-12" data-testid="reset-confirm-password-input"
                />
              </div>
              {pw && confirm && (
                <p className={`text-xs flex items-center gap-1 ${pw === confirm ? 'text-green-600' : 'text-red-600'}`}>
                  {pw === confirm ? <><CheckCircle2 className="w-3 h-3" /> {t('resetPassword.match', 'Passordene stemmer')}</> : <><AlertCircle className="w-3 h-3" /> {t('resetPassword.noMatch', 'Passordene stemmer ikke')}</>}
                </p>
              )}
              <Button type="submit" disabled={loading || pw !== confirm} className="w-full h-12 font-semibold" data-testid="reset-submit-btn">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : t('resetPassword.submit', 'Oppdater passord')}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
